/**
 * Budget Simulation Page - Predictive Spend 2.0
 *
 * Revolutionary optimization UI that transforms predictive spend from reporting mode
 * to optimization mode. Full-page experience with beautiful visualizations.
 *
 * @author Sonny Sparks
 * @version 3.0.1
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { useUserStore } from "@/stores";
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	BarChartOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	DollarOutlined,
	DownloadOutlined,
	FireOutlined,
	PieChartOutlined,
	ThunderboltOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Document,
	Image as PDFImage,
	Text as PDFText,
	Page,
	StyleSheet,
	View,
	pdf,
} from "@react-pdf/renderer";
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	InputNumber,
	Progress,
	Row,
	Select,
	Space,
	Spin,
	Statistic,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from "antd";
import {
	ArcElement,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Title as ChartTitle,
	Tooltip as ChartTooltip,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
} from "chart.js";
import { useState } from "react";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import "./BudgetSimulationPage.scss";

// Register ChartJS components
ChartJS.register(
	ArcElement,
	ChartTooltip,
	Legend,
	CategoryScale,
	LinearScale,
	BarElement,
	ChartTitle,
	PointElement,
	LineElement,
);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// ===================================
// INTERFACES
// ===================================

interface OptimizationAsset {
	id: number;
	assetTag?: string;
	assetType: string;
	assetFamily: string;
	location: string;
	replacementCost: number;
	rpn: number;
	rpnPriority: "Critical" | "High" | "Medium" | "Low";
	criticalityScore: number;
	effectiveAge: number;
	healthScore: number;
}

interface OptimizationResult {
	fundedAssets: OptimizationAsset[];
	deferredAssets: OptimizationAsset[];
	summary: {
		totalBudget: number;
		budgetUsed: number;
		budgetRemaining: number;
		fundedCount: number;
		deferredCount: number;
		fundedRiskScore: number;
		deferredRiskScore: number;
		totalRiskReduction: number;
	};
	warnings: {
		severity: "critical" | "high" | "medium" | "low";
		message: string;
		assetId?: number;
		assetTag?: string;
		riskImpact?: number;
	}[];
}

// ===================================
// MAIN COMPONENT
// ===================================

export const BudgetSimulationPage = () => {
	const { user } = useUserStore();

	// State
	const [budgetCap, setBudgetCap] = useState<number>(5000000);
	const [timeHorizon, setTimeHorizon] = useState<number>(5);
	const [strategy, setStrategy] = useState<
		"maximize-criticality" | "minimize-risk" | "balanced"
	>("maximize-criticality");
	const [minCriticalityScore, setMinCriticalityScore] = useState<number | null>(
		null,
	);
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<OptimizationResult | null>(null);

	// Run optimization
	const runOptimization = async () => {
		if (!user?.organization?.id) {
			message.error("No organization selected");
			return;
		}

		setLoading(true);
		try {
			const token = await auth.currentUser?.getIdToken();
			const response = await fetch(
				`${VITE_CORE_API_URL}${CORE_URL}/organization/predspend/${user.organization.id}/optimize`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						budgetCap,
						timeHorizon,
						strategy,
						constraints: {
							minCriticalityScore: minCriticalityScore || undefined,
						},
					}),
				},
			);

			if (!response.ok) {
				throw new Error("Failed to run optimization");
			}

			const data = await response.json();
			setResult(data);
			message.success("Optimization completed successfully");
		} catch (error) {
			console.error("Error running optimization:", error);
			message.error("Failed to run optimization. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	// PDF Export Handler
	const handleExportPDF = async () => {
		if (!result) {
			message.error("No optimization results to export");
			return;
		}

		try {
			message.loading("Generating PDF with charts...", 0);

			// Capture chart images
			const chartImages = await captureChartImages();

			const pdfBlob = await generateBudgetSimulationPDF(
				result,
				budgetCap,
				timeHorizon,
				strategy,
				minCriticalityScore,
				chartImages,
			);

			// Create download link
			const url = window.URL.createObjectURL(pdfBlob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `budget-simulation-${new Date().toISOString().split("T")[0]}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			message.destroy();
			message.success("PDF exported successfully");
		} catch (error) {
			message.destroy();
			console.error("Error generating PDF:", error);
			message.error("Failed to generate PDF");
		}
	};

	// Capture chart images as data URLs
	const captureChartImages = async () => {
		const images: Record<string, string> = {};

		try {
			// Find all chart canvas elements
			const chartContainers = document.querySelectorAll(
				".chart-container canvas",
			);

			if (chartContainers.length >= 4) {
				// Budget Allocation (Doughnut)
				images.budgetAllocation = (
					chartContainers[0] as HTMLCanvasElement
				).toDataURL("image/png");
				// Funded vs Deferred (Pie)
				images.fundedVsDeferred = (
					chartContainers[1] as HTMLCanvasElement
				).toDataURL("image/png");
				// Risk Distribution (Pie)
				images.riskDistribution = (
					chartContainers[2] as HTMLCanvasElement
				).toDataURL("image/png");
				// Asset Family Breakdown (Bar)
				images.assetFamilyBreakdown = (
					chartContainers[3] as HTMLCanvasElement
				).toDataURL("image/png");
			}
		} catch (error) {
			console.warn("Could not capture chart images:", error);
		}

		return images;
	};

	// Generate PDF document
	const generateBudgetSimulationPDF = async (
		result: OptimizationResult,
		budgetCap: number,
		timeHorizon: number,
		strategy: string,
		minCriticalityScore: number | null,
		chartImages?: Record<string, string>,
	) => {
		const styles = StyleSheet.create({
			page: {
				flexDirection: "column",
				backgroundColor: "#ffffff",
				padding: 30,
				fontSize: 10,
				fontFamily: "Helvetica",
			},
			header: {
				marginBottom: 20,
				paddingBottom: 15,
				borderBottom: "2 solid #4f46e5",
			},
			title: {
				fontSize: 20,
				fontWeight: "bold",
				color: "#1e293b",
				marginBottom: 5,
			},
			subtitle: {
				fontSize: 11,
				color: "#64748b",
				marginBottom: 10,
			},
			sectionTitle: {
				fontSize: 14,
				fontWeight: "bold",
				marginTop: 15,
				marginBottom: 10,
				color: "#1e293b",
				backgroundColor: "#f1f5f9",
				padding: 8,
			},
			row: {
				flexDirection: "row",
				marginBottom: 5,
			},
			label: {
				width: "40%",
				fontWeight: "bold",
				color: "#475569",
			},
			value: {
				width: "60%",
				color: "#1e293b",
			},
			statsGrid: {
				flexDirection: "row",
				justifyContent: "space-between",
				marginBottom: 15,
				flexWrap: "wrap",
			},
			statBox: {
				width: "48%",
				backgroundColor: "#f8fafc",
				padding: 10,
				marginBottom: 10,
				borderRadius: 4,
				border: "1 solid #e2e8f0",
			},
			statLabel: {
				fontSize: 9,
				color: "#64748b",
				marginBottom: 3,
			},
			statValue: {
				fontSize: 16,
				fontWeight: "bold",
				color: "#1e293b",
			},
			table: {
				marginTop: 10,
				marginBottom: 15,
			},
			tableHeader: {
				flexDirection: "row",
				backgroundColor: "#f1f5f9",
				padding: 8,
				fontWeight: "bold",
				borderBottom: "1 solid #cbd5e1",
			},
			tableRow: {
				flexDirection: "row",
				padding: 6,
				borderBottom: "1 solid #e2e8f0",
			},
			tableCell: {
				fontSize: 8,
			},
			footer: {
				position: "absolute",
				bottom: 30,
				left: 30,
				right: 30,
				textAlign: "center",
				fontSize: 8,
				color: "#94a3b8",
				borderTop: "1 solid #e2e8f0",
				paddingTop: 10,
			},
		});

		const formatCurrency = (value: number) => {
			return new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
				minimumFractionDigits: 0,
			}).format(value);
		};

		const getStrategyLabel = (strategy: string) => {
			switch (strategy) {
				case "maximize-criticality":
					return "Maximize Criticality";
				case "minimize-risk":
					return "Minimize Risk";
				case "balanced":
					return "Balanced Approach";
				default:
					return strategy;
			}
		};

		const document = (
			<Document>
				<Page size="A4" style={styles.page}>
					{/* Header */}
					<View style={styles.header}>
						<PDFText style={styles.title}>Budget Optimization Report</PDFText>
						<PDFText style={styles.subtitle}>
							Generated on {new Date().toLocaleDateString()} at{" "}
							{new Date().toLocaleTimeString()}
						</PDFText>
					</View>

					{/* Configuration Parameters */}
					<PDFText style={styles.sectionTitle}>Configuration</PDFText>
					<View style={styles.row}>
						<PDFText style={styles.label}>Budget Cap:</PDFText>
						<PDFText style={styles.value}>{formatCurrency(budgetCap)}</PDFText>
					</View>
					<View style={styles.row}>
						<PDFText style={styles.label}>Time Horizon:</PDFText>
						<PDFText style={styles.value}>{timeHorizon} years</PDFText>
					</View>
					<View style={styles.row}>
						<PDFText style={styles.label}>Strategy:</PDFText>
						<PDFText style={styles.value}>{getStrategyLabel(strategy)}</PDFText>
					</View>
					{minCriticalityScore && (
						<View style={styles.row}>
							<PDFText style={styles.label}>Min Criticality Score:</PDFText>
							<PDFText style={styles.value}>{minCriticalityScore}</PDFText>
						</View>
					)}

					{/* Summary Statistics */}
					<PDFText style={styles.sectionTitle}>Optimization Results</PDFText>
					<PDFText style={{ fontSize: 8, color: "#64748b", marginBottom: 10 }}>
						* RPN (Risk Priority Number) = Severity × Occurrence × Detection
					</PDFText>
					<View style={styles.statsGrid}>
						<View style={styles.statBox}>
							<PDFText style={styles.statLabel}>Budget Used</PDFText>
							<PDFText style={styles.statValue}>
								{formatCurrency(result.summary.budgetUsed)}
							</PDFText>
						</View>
						<View style={styles.statBox}>
							<PDFText style={styles.statLabel}>Budget Remaining</PDFText>
							<PDFText style={styles.statValue}>
								{formatCurrency(result.summary.budgetRemaining)}
							</PDFText>
						</View>
						<View style={styles.statBox}>
							<PDFText style={styles.statLabel}>Assets Funded</PDFText>
							<PDFText style={styles.statValue}>
								{result.summary.fundedCount} /{" "}
								{result.summary.fundedCount + result.summary.deferredCount}
							</PDFText>
						</View>
						<View style={styles.statBox}>
							<PDFText style={styles.statLabel}>Risk Reduction</PDFText>
							<PDFText style={styles.statValue}>
								{result.summary.totalRiskReduction.toFixed(1)}%
							</PDFText>
						</View>
					</View>

					{/* Charts Section */}
					{chartImages && Object.keys(chartImages).length > 0 && (
						<>
							<PDFText style={styles.sectionTitle}>
								Budget Visualization
							</PDFText>
							<View
								style={{
									flexDirection: "row",
									flexWrap: "wrap",
									justifyContent: "space-between",
									marginBottom: 20,
								}}
							>
								{chartImages.budgetAllocation && (
									<View style={{ width: "48%", marginBottom: 10 }}>
										<PDFText
											style={{
												fontSize: 10,
												fontWeight: "bold",
												marginBottom: 5,
											}}
										>
											Budget Allocation
										</PDFText>
										<PDFImage
											src={chartImages.budgetAllocation}
											style={{ width: "100%", height: 150 }}
										/>
									</View>
								)}
								{chartImages.fundedVsDeferred && (
									<View style={{ width: "48%", marginBottom: 10 }}>
										<PDFText
											style={{
												fontSize: 10,
												fontWeight: "bold",
												marginBottom: 5,
											}}
										>
											Funded vs Deferred Assets
										</PDFText>
										<PDFImage
											src={chartImages.fundedVsDeferred}
											style={{ width: "100%", height: 150 }}
										/>
									</View>
								)}
								{chartImages.riskDistribution && (
									<View style={{ width: "48%", marginBottom: 10 }}>
										<PDFText
											style={{
												fontSize: 10,
												fontWeight: "bold",
												marginBottom: 5,
											}}
										>
											Risk Priority Distribution
										</PDFText>
										<PDFImage
											src={chartImages.riskDistribution}
											style={{ width: "100%", height: 150 }}
										/>
									</View>
								)}
								{chartImages.assetFamilyBreakdown && (
									<View style={{ width: "48%", marginBottom: 10 }}>
										<PDFText
											style={{
												fontSize: 10,
												fontWeight: "bold",
												marginBottom: 5,
											}}
										>
											Top Asset Families (Funded)
										</PDFText>
										<PDFImage
											src={chartImages.assetFamilyBreakdown}
											style={{ width: "100%", height: 150 }}
										/>
									</View>
								)}
							</View>
						</>
					)}

					{/* Funded Projects - Top 10 */}
					<PDFText style={styles.sectionTitle}>
						Funded Projects (Top 10 by Cost)
					</PDFText>
					<View style={styles.table}>
						<View style={styles.tableHeader}>
							<PDFText style={{ ...styles.tableCell, width: "15%" }}>
								Asset Tag
							</PDFText>
							<PDFText style={{ ...styles.tableCell, width: "20%" }}>
								Type
							</PDFText>
							<PDFText style={{ ...styles.tableCell, width: "20%" }}>
								Family
							</PDFText>
							<PDFText style={{ ...styles.tableCell, width: "15%" }}>
								Priority
							</PDFText>
							<PDFText style={{ ...styles.tableCell, width: "15%" }}>
								RPN
							</PDFText>
							<PDFText style={{ ...styles.tableCell, width: "15%" }}>
								Cost
							</PDFText>
						</View>
						{result.fundedAssets
							.sort((a, b) => b.replacementCost - a.replacementCost)
							.slice(0, 10)
							.map((asset) => (
								<View key={asset.id} style={styles.tableRow}>
									<PDFText style={{ ...styles.tableCell, width: "15%" }}>
										{asset.assetTag}
									</PDFText>
									<PDFText style={{ ...styles.tableCell, width: "20%" }}>
										{asset.assetType}
									</PDFText>
									<PDFText style={{ ...styles.tableCell, width: "20%" }}>
										{asset.assetFamily}
									</PDFText>
									<PDFText style={{ ...styles.tableCell, width: "15%" }}>
										{asset.rpnPriority}
									</PDFText>
									<PDFText style={{ ...styles.tableCell, width: "15%" }}>
										{asset.rpn.toFixed(2)}
									</PDFText>
									<PDFText style={{ ...styles.tableCell, width: "15%" }}>
										{formatCurrency(asset.replacementCost)}
									</PDFText>
								</View>
							))}
					</View>

					{/* Footer */}
					<PDFText style={styles.footer}>
						FacilityAI Budget Optimization Report • Page 1
					</PDFText>
				</Page>

				{/* Second Page - Deferred Projects if there are many */}
				{result.deferredAssets.length > 0 && (
					<Page size="A4" style={styles.page}>
						<PDFText style={styles.sectionTitle}>
							Deferred Projects (Top 10 by Risk)
						</PDFText>
						<View style={styles.table}>
							<View style={styles.tableHeader}>
								<PDFText style={{ ...styles.tableCell, width: "15%" }}>
									Asset Tag
								</PDFText>
								<PDFText style={{ ...styles.tableCell, width: "20%" }}>
									Type
								</PDFText>
								<PDFText style={{ ...styles.tableCell, width: "20%" }}>
									Family
								</PDFText>
								<PDFText style={{ ...styles.tableCell, width: "15%" }}>
									Priority
								</PDFText>
								<PDFText style={{ ...styles.tableCell, width: "15%" }}>
									RPN
								</PDFText>
								<PDFText style={{ ...styles.tableCell, width: "15%" }}>
									Cost
								</PDFText>
							</View>
							{result.deferredAssets
								.sort((a, b) => b.rpn - a.rpn)
								.slice(0, 10)
								.map((asset) => (
									<View key={asset.id} style={styles.tableRow}>
										<PDFText style={{ ...styles.tableCell, width: "15%" }}>
											{asset.assetTag}
										</PDFText>
										<PDFText style={{ ...styles.tableCell, width: "20%" }}>
											{asset.assetType}
										</PDFText>
										<PDFText style={{ ...styles.tableCell, width: "20%" }}>
											{asset.assetFamily}
										</PDFText>
										<PDFText style={{ ...styles.tableCell, width: "15%" }}>
											{asset.rpnPriority}
										</PDFText>
										<PDFText style={{ ...styles.tableCell, width: "15%" }}>
											{asset.rpn.toFixed(2)}
										</PDFText>
										<PDFText style={{ ...styles.tableCell, width: "15%" }}>
											{formatCurrency(asset.replacementCost)}
										</PDFText>
									</View>
								))}
						</View>

						{/* Warnings Section */}
						{result.warnings.length > 0 && (
							<>
								<PDFText style={styles.sectionTitle}>Risk Warnings</PDFText>
								{result.warnings.slice(0, 10).map((warning, index) => (
									<View
										key={`warning-${warning.assetId || index}-${index}`}
										style={styles.row}
									>
										<PDFText
											style={{
												...styles.value,
												width: "100%",
												color:
													warning.severity === "critical"
														? "#dc2626"
														: "#f59e0b",
												marginBottom: 4,
											}}
										>
											• {warning.message}
										</PDFText>
									</View>
								))}
							</>
						)}

						<PDFText style={styles.footer}>
							FacilityAI Budget Optimization Report • Page 2
						</PDFText>
					</Page>
				)}
			</Document>
		);

		return await pdf(document).toBlob();
	};

	// ===================================
	// CHART DATA PREPARATION
	// ===================================

	const getRiskDistributionData = () => {
		if (!result) return null;

		const priorities = { Critical: 0, High: 0, Medium: 0, Low: 0 };

		[...result.fundedAssets, ...result.deferredAssets].forEach((asset) => {
			priorities[asset.rpnPriority]++;
		});

		return {
			labels: ["Critical", "High", "Medium", "Low"],
			datasets: [
				{
					data: [
						priorities.Critical,
						priorities.High,
						priorities.Medium,
						priorities.Low,
					],
					backgroundColor: ["#AC2C2B", "#C84249", "#CF7180", "#8A2E8F"],
					borderColor: ["#AC2C2B", "#C84249", "#CF7180", "#8A2E8F"],
					borderWidth: 1,
				},
			],
		};
	};

	const getBudgetAllocationData = () => {
		if (!result) return null;

		return {
			labels: ["Budget Used", "Budget Remaining"],
			datasets: [
				{
					data: [result.summary.budgetUsed, result.summary.budgetRemaining],
					backgroundColor: ["#166534", "#e5e7eb"],
					borderColor: ["#166534", "#e5e7eb"],
					borderWidth: 1,
				},
			],
		};
	};

	const getAssetFamilyBreakdownData = () => {
		if (!result) return null;

		const familyCosts: Record<string, number> = {};

		result.fundedAssets.forEach((asset) => {
			familyCosts[asset.assetFamily] =
				(familyCosts[asset.assetFamily] || 0) + asset.replacementCost;
		});

		const sortedFamilies = Object.entries(familyCosts)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 8); // Top 8 families

		// Use the same color palette as PredSpendChart
		const assetColors = [
			"#AC2C2B",
			"#8A2E8F",
			"#C84249",
			"#732B5C",
			"#CF7180",
			"#954580",
			"#C95D76",
			"#B33B42",
		];

		return {
			labels: sortedFamilies.map(([family]) => family),
			datasets: [
				{
					label: "Replacement Cost",
					data: sortedFamilies.map(([, cost]) => cost),
					backgroundColor: sortedFamilies.map(
						(_, idx) => assetColors[idx % assetColors.length],
					),
					borderColor: sortedFamilies.map(
						(_, idx) => assetColors[idx % assetColors.length],
					),
					borderWidth: 1,
				},
			],
		};
	};

	const getFundedVsDeferredData = () => {
		if (!result) return null;

		return {
			labels: ["Funded", "Deferred"],
			datasets: [
				{
					data: [result.summary.fundedCount, result.summary.deferredCount],
					backgroundColor: ["#166534", "#AC2C2B"],
					borderColor: ["#166534", "#AC2C2B"],
					borderWidth: 1,
				},
			],
		};
	};

	// Chart options
	const pieOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "bottom" as const,
				labels: {
					padding: 15,
					font: {
						size: 12,
					},
				},
			},
			tooltip: {
				callbacks: {
					label: (context: {
						label?: string;
						parsed?: number;
						dataset: { data: number[] };
					}) => {
						const label = context.label || "";
						const value = context.parsed || 0;
						const total = context.dataset.data.reduce(
							(acc: number, val: number) => acc + val,
							0,
						);
						const percentage = ((value / total) * 100).toFixed(1);
						return `${label}: ${value} (${percentage}%)`;
					},
				},
			},
		},
	};

	const doughnutOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "bottom" as const,
				labels: {
					padding: 15,
					font: {
						size: 12,
					},
				},
			},
			tooltip: {
				callbacks: {
					label: (context: { label?: string; parsed?: number }) => {
						const label = context.label || "";
						const value = context.parsed || 0;
						return `${label}: $${value.toLocaleString()}`;
					},
				},
			},
		},
		cutout: "60%",
	};

	const barOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: (context: { parsed: { y?: number } }) => {
						const value = context.parsed.y || 0;
						return `Cost: $${value.toLocaleString()}`;
					},
				},
			},
		},
		scales: {
			y: {
				beginAtZero: true,
				ticks: {
					callback: (value: number | string) => {
						const numValue =
							typeof value === "string" ? Number.parseFloat(value) : value;
						return `$${(numValue / 1000000).toFixed(1)}M`;
					},
				},
			},
		},
	};

	// Table columns for funded assets
	const fundedColumns = [
		{
			title: "Asset Tag",
			dataIndex: "assetTag",
			key: "assetTag",
			render: (tag: string | undefined, record: OptimizationAsset) =>
				tag || `Asset ${record.id}`,
		},
		{
			title: "Type",
			dataIndex: "assetType",
			key: "assetType",
		},
		{
			title: "Family",
			dataIndex: "assetFamily",
			key: "assetFamily",
			render: (family: string) => <Tag color="blue">{family}</Tag>,
		},
		{
			title: "Location",
			dataIndex: "location",
			key: "location",
		},
		{
			title: "Priority",
			dataIndex: "rpnPriority",
			key: "rpnPriority",
			render: (priority: string) => {
				const colors = {
					Critical: "red",
					High: "orange",
					Medium: "gold",
					Low: "blue",
				};
				return (
					<Tag color={colors[priority as keyof typeof colors]}>{priority}</Tag>
				);
			},
		},
		{
			title: (
				<Tooltip title="Risk Priority Number (RPN) = Severity × Occurrence × Detection. Higher values indicate greater risk.">
					RPN <span style={{ fontSize: "10px", opacity: 0.7 }}>ⓘ</span>
				</Tooltip>
			),
			dataIndex: "rpn",
			key: "rpn",
			render: (rpn: number) => rpn.toFixed(2),
			sorter: (a: OptimizationAsset, b: OptimizationAsset) => b.rpn - a.rpn,
		},
		{
			title: "Cost",
			dataIndex: "replacementCost",
			key: "replacementCost",
			render: (cost: number) => `$${cost.toLocaleString()}`,
			sorter: (a: OptimizationAsset, b: OptimizationAsset) =>
				b.replacementCost - a.replacementCost,
		},
	];

	// Table columns for deferred assets
	const deferredColumns = [
		...fundedColumns,
		{
			title: "Risk Impact",
			key: "riskImpact",
			render: (_: unknown, record: OptimizationAsset) => {
				const warning = result?.warnings.find((w) => w.assetId === record.id);
				if (warning?.riskImpact) {
					return (
						<Tooltip title={warning.message}>
							<Tag color="red" icon={<WarningOutlined />}>
								+{warning.riskImpact}%
							</Tag>
						</Tooltip>
					);
				}
				return <Tag color="orange">High Risk</Tag>;
			},
		},
	];

	// ===================================
	// RENDER
	// ===================================

	return (
		<div className="budget-simulation-page">
			{/* Header */}
			<div className="page-header">
				<div className="header-content">
					<div className="header-top">
						<div className="header-title-section">
							<Title level={2}>
								<BarChartOutlined /> Budget Optimization Simulator
							</Title>
						</div>
						{result && (
							<Button
								icon={<DownloadOutlined />}
								onClick={handleExportPDF}
								className="export-plan-btn"
							>
								Export Plan
							</Button>
						)}
					</div>
					<Paragraph className="header-description">
						Transform your predictive spend from reporting to strategic
						optimization. Input budget constraints and receive AI-powered asset
						replacement recommendations that maximize risk reduction within your
						financial limits.
					</Paragraph>
				</div>
			</div>

			{/* Configuration Section */}
			<Card
				className="config-card"
				title={
					<>
						<ThunderboltOutlined /> Scenario Configuration
					</>
				}
			>
				<Row gutter={24}>
					<Col xs={24} sm={12} lg={6}>
						<div className="config-item">
							<Text strong>Budget Cap</Text>
							<InputNumber
								value={budgetCap}
								onChange={(val) => setBudgetCap(val || 0)}
								formatter={(value) =>
									`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
								}
								parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
								style={{ width: "100%" }}
								min={0}
								size="large"
							/>
						</div>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<div className="config-item">
							<Text strong>Time Horizon (Years)</Text>
							<InputNumber
								value={timeHorizon}
								onChange={(val) => setTimeHorizon(val || 5)}
								style={{ width: "100%" }}
								min={1}
								max={20}
								size="large"
							/>
						</div>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<div className="config-item">
							<Text strong>Optimization Strategy</Text>
							<Select
								value={strategy}
								onChange={setStrategy}
								style={{ width: "100%" }}
								size="large"
							>
								<Option value="maximize-criticality">
									<Space>
										<FireOutlined style={{ color: "#ff4d4f" }} />
										Maximize Criticality
									</Space>
								</Option>
								<Option value="minimize-risk">
									<Space>
										<WarningOutlined style={{ color: "#faad14" }} />
										Minimize Risk
									</Space>
								</Option>
								<Option value="balanced">
									<Space>
										<BarChartOutlined style={{ color: "#1890ff" }} />
										Balanced
									</Space>
								</Option>
							</Select>
						</div>
					</Col>
					<Col xs={24} sm={12} lg={6}>
						<div className="config-item">
							<Text strong>Minimum Criticality</Text>
							<Select
								value={minCriticalityScore}
								onChange={setMinCriticalityScore}
								style={{ width: "100%" }}
								allowClear
								placeholder="All assets"
								size="large"
							>
								<Option value={5}>5 - Mission Critical Only</Option>
								<Option value={4}>4+ High Impact & Above</Option>
								<Option value={3}>3+ Standard & Above</Option>
							</Select>
						</div>
					</Col>
				</Row>

				<Divider />

				<div className="strategy-description">
					<Alert
						message={
							strategy === "maximize-criticality"
								? "Prioritizes mission-critical assets (elevators, boilers) for maximum operational continuity"
								: strategy === "minimize-risk"
									? "Focuses on assets most likely to fail soon, reducing immediate risk exposure"
									: "Balances cost efficiency, criticality, and failure probability for optimal overall outcomes"
						}
						type="info"
						showIcon
					/>
				</div>

				<Divider />

				<Button
					type="primary"
					icon={<ThunderboltOutlined />}
					onClick={runOptimization}
					loading={loading}
					size="large"
					block
					className="run-optimization-btn"
				>
					Run Optimization
				</Button>
			</Card>

			{/* Loading State */}
			{loading && (
				<Card className="loading-card">
					<div className="loading-content">
						<Spin size="large" />
						<Title level={4} style={{ marginTop: 24 }}>
							Running Optimization Algorithm...
						</Title>
						<Paragraph>
							Analyzing asset conditions, calculating risk priorities, and
							optimizing your budget allocation.
						</Paragraph>
					</div>
				</Card>
			)}

			{/* Results Section */}
			{result && !loading && (
				<>
					{/* Summary Statistics */}
					<Card
						className="summary-card"
						title={
							<>
								<PieChartOutlined /> Optimization Results
							</>
						}
					>
						<Row gutter={24}>
							<Col xs={24} sm={12} lg={6}>
								<Card className="stat-card stat-card-success">
									<Statistic
										title="Budget Used"
										value={result.summary.budgetUsed}
										precision={0}
										prefix={<DollarOutlined />}
										valueStyle={{ color: "#166534", fontWeight: "bold" }}
									/>
									<Progress
										percent={Math.round(
											(result.summary.budgetUsed / result.summary.totalBudget) *
												100,
										)}
										status="active"
										strokeColor="#166534"
									/>
								</Card>
							</Col>
							<Col xs={24} sm={12} lg={6}>
								<Card className="stat-card stat-card-info">
									<Statistic
										title="Budget Remaining"
										value={result.summary.budgetRemaining}
										precision={0}
										prefix={<DollarOutlined />}
										valueStyle={{ color: "#1890ff", fontWeight: "bold" }}
									/>
									<Progress
										percent={Math.round(
											(result.summary.budgetRemaining /
												result.summary.totalBudget) *
												100,
										)}
										strokeColor="#1890ff"
									/>
								</Card>
							</Col>
							<Col xs={24} sm={12} lg={6}>
								<Card className="stat-card stat-card-funded">
									<Statistic
										title="Assets Funded"
										value={result.summary.fundedCount}
										prefix={<CheckCircleOutlined />}
										suffix={`/ ${result.summary.fundedCount + result.summary.deferredCount}`}
										valueStyle={{ color: "#166534", fontWeight: "bold" }}
									/>
									<div className="stat-footer">
										<Text style={{ color: "#166534" }}>
											<ArrowUpOutlined />{" "}
											{Math.round(
												(result.summary.fundedCount /
													(result.summary.fundedCount +
														result.summary.deferredCount)) *
													100,
											)}
											% Coverage
										</Text>
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} lg={6}>
								<Card className="stat-card stat-card-risk">
									<Statistic
										title="Risk Reduction"
										value={result.summary.totalRiskReduction}
										precision={1}
										suffix="%"
										prefix={<FireOutlined />}
										valueStyle={{ color: "#cf1322", fontWeight: "bold" }}
									/>
									<div className="stat-footer">
										<Text type="danger">
											<ArrowDownOutlined /> Risk Decreased
										</Text>
									</div>
								</Card>
							</Col>
						</Row>
					</Card>

					{/* Charts Section - Top Row */}
					<Row
						gutter={24}
						className="charts-section"
						style={{ marginTop: "32px" }}
					>
						<Col xs={24} lg={12}>
							<Card
								title={
									<>
										<PieChartOutlined /> Budget Allocation
									</>
								}
								className="chart-card"
							>
								<div className="chart-container">
									{getBudgetAllocationData() && (
										<Doughnut
											data={getBudgetAllocationData()!}
											options={doughnutOptions}
										/>
									)}
								</div>
							</Card>
						</Col>
						<Col xs={24} lg={12}>
							<Card
								title={
									<>
										<PieChartOutlined /> Funded vs Deferred Assets
									</>
								}
								className="chart-card"
							>
								<div className="chart-container">
									{getFundedVsDeferredData() && (
										<Pie
											data={getFundedVsDeferredData()!}
											options={pieOptions}
										/>
									)}
								</div>
							</Card>
						</Col>
					</Row>

					{/* Charts Section - Bottom Row */}
					<Row
						gutter={24}
						className="charts-section"
						style={{ marginTop: "48px" }}
					>
						<Col xs={24} lg={12}>
							<Card
								title={
									<>
										<PieChartOutlined /> Risk Priority Distribution
									</>
								}
								className="chart-card"
							>
								<div className="chart-container">
									{getRiskDistributionData() && (
										<Pie
											data={getRiskDistributionData()!}
											options={pieOptions}
										/>
									)}
								</div>
							</Card>
						</Col>
						<Col xs={24} lg={12}>
							<Card
								title={
									<>
										<BarChartOutlined /> Top Asset Families (Funded)
									</>
								}
								className="chart-card"
							>
								<div className="chart-container">
									{getAssetFamilyBreakdownData() && (
										<Bar
											data={getAssetFamilyBreakdownData()!}
											options={barOptions}
										/>
									)}
								</div>
							</Card>
						</Col>
					</Row>

					{/* Warnings */}
					{result.warnings.length > 0 && (
						<Alert
							className="warnings-alert"
							message={
								<>
									<WarningOutlined /> Risk Warnings ({result.warnings.length})
								</>
							}
							description={
								<ul className="warnings-list">
									{result.warnings.slice(0, 5).map((warning) => (
										<li key={`${warning.assetId}-${warning.message}`}>
											<Text
												type={
													warning.severity === "critical" ? "danger" : "warning"
												}
											>
												{warning.message}
											</Text>
										</li>
									))}
									{result.warnings.length > 5 && (
										<li key="more-warnings">
											<Text type="secondary">
												... and {result.warnings.length - 5} more warnings
											</Text>
										</li>
									)}
								</ul>
							}
							type="warning"
							showIcon
						/>
					)}

					{/* Funded Assets Table */}
					<Card
						className="table-card funded-table-card"
						title={
							<Space>
								<CheckCircleOutlined style={{ color: "#166534" }} />
								<span>Funded Projects ({result.summary.fundedCount})</span>
								<Tag color="green">
									Risk Score: {result.summary.fundedRiskScore.toFixed(1)}/100
								</Tag>
							</Space>
						}
					>
						<Table
							dataSource={result.fundedAssets}
							columns={fundedColumns}
							rowKey="id"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showTotal: (total) => `Total ${total} assets`,
							}}
							size="small"
							scroll={{ x: "max-content" }}
						/>
					</Card>

					{/* Deferred Assets Table */}
					<Card
						className="table-card deferred-table-card"
						title={
							<Space>
								<CloseCircleOutlined style={{ color: "#cf1322" }} />
								<span>Deferred Backlog ({result.summary.deferredCount})</span>
								<Tag color="red">
									Risk Score: {result.summary.deferredRiskScore.toFixed(1)}/100
								</Tag>
							</Space>
						}
					>
						<Alert
							message="These assets were not funded due to budget constraints and represent accepted risk."
							type="error"
							showIcon
							style={{ marginBottom: 16 }}
						/>
						<Table
							dataSource={result.deferredAssets}
							columns={deferredColumns}
							rowKey="id"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showTotal: (total) => `Total ${total} assets`,
							}}
							size="small"
							scroll={{ x: "max-content" }}
						/>
					</Card>
				</>
			)}

			{/* Empty State */}
			{!result && !loading && (
				<Card className="empty-state-card">
					<div className="empty-state-content">
						<BarChartOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
						<Title level={4}>Ready to Optimize Your Budget</Title>
						<Paragraph>
							Configure your scenario parameters above and click "Run
							Optimization" to get started. The algorithm will analyze your
							assets and provide optimal replacement recommendations within your
							budget constraints.
						</Paragraph>
					</div>
				</Card>
			)}
		</div>
	);
};
